import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getMpiTierDecimal } from "@/lib/mpiTiers";
import { ClubCrest } from "@/components/ClubCrest";
import { Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";


interface HeroBandProps {
  leagueCount: number;
  onSelectManager: (managerId: string, managerSlug?: string) => void;
  children: React.ReactNode;
}

interface HotEntry {
  managerId: string;
  managerSlug: string | null;
  managerName: string;
  clubName: string;
  primary: string | null;
  secondary: string | null;
  score: number;
  lastMatchDate: string | null;
}

export function HeroBand({ leagueCount, onSelectManager, children }: HeroBandProps) {
  const { data: hottest = [] } = useQuery({
    queryKey: ["hero-hottest-seats"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<HotEntry[]> => {
      // Only scores built from a recent match count: a sacked manager's tenure
      // stops accruing results, so his row goes stale within a round or two
      // even when the provider has not yet closed the assignment.
      const freshnessCutoff = new Date(Date.now() - 14 * 86400000)
        .toISOString()
        .slice(0, 10);

      const [scoresRes, participantsRes, leaguesRes, alertsRes] = await Promise.all([
        supabase
          .from("manager_hot_seat_scores")
          .select(
            "manager_id, club_id, league, hot_seat_score, played, last_match_date, managers(id, name, slug), clubs(name, brand_primary, brand_secondary), manager_assignments!inner(end_date, start_date)",
          )
          .gte("played", 5)
          .gte("last_match_date", freshnessCutoff)
          .order("hot_seat_score", { ascending: false })
          .limit(200),
        supabase.from("season_participants").select("league, season, club_id"),
        supabase.from("leagues").select("code, is_active"),
        supabase
          .from("manager_change_alerts")
          .select("club_id, api_manager_name, db_manager_id")
          .eq("status", "open"),
      ]);
      if (scoresRes.error) throw scoresRes.error;

      const activeLeagues = new Set(
        (leaguesRes.data || []).filter((l: any) => l.is_active).map((l: any) => l.code),
      );

      // Clubs where the provider currently names a different manager than we do.
      const disputedClubs = new Set(
        (alertsRes.data || [])
          .filter((a: any) => a.api_manager_name)
          .map((a: any) => a.club_id),
      );

      // Latest season label per league (short labels sort lexicographically)
      const latestSeason = new Map<string, string>();
      for (const p of participantsRes.data || []) {
        const cur = latestSeason.get(p.league);
        if (!cur || p.season > cur) latestSeason.set(p.league, p.season);
      }
      const currentRoster = new Set(
        (participantsRes.data || [])
          .filter((p: any) => latestSeason.get(p.league) === p.season)
          .map((p: any) => `${p.league}|${p.club_id}`),
      );

      // Only genuinely current tenures at clubs on this season's roster of an
      // active league, and at most one row per manager.
      const open = (scoresRes.data || []).filter(
        (row: any) =>
          row.manager_assignments?.end_date === null &&
          activeLeagues.has(row.league) &&
          currentRoster.has(`${row.league}|${row.club_id}`) &&
          !disputedClubs.has(row.club_id),
      );
      const bestByManager = new Map<string, any>();
      for (const row of open) {
        const existing = bestByManager.get(row.manager_id);
        if (
          !existing ||
          (row.manager_assignments?.start_date ?? "") >
            (existing.manager_assignments?.start_date ?? "")
        ) {
          bestByManager.set(row.manager_id, row);
        }
      }


      return Array.from(bestByManager.values())
        .sort((a: any, b: any) => b.hot_seat_score - a.hot_seat_score)
        .slice(0, 3)
        .map((row: any) => ({
          managerId: row.manager_id,
          managerSlug: row.managers?.slug ?? null,
          managerName: row.managers?.name ?? "Unknown",
          clubName: row.clubs?.name ?? "",
          primary: row.clubs?.brand_primary ?? null,
          secondary: row.clubs?.brand_secondary ?? null,
          score: row.hot_seat_score,
          lastMatchDate: row.last_match_date ?? null,
        }));
    },
  });

  const resultsUpTo = hottest
    .map((e) => e.lastMatchDate)
    .filter((d): d is string => Boolean(d))
    .sort()
    .pop();



  return (
    <section className="chrome-band relative z-20">
      {/* faint pitch stripes */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.07]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, hsl(var(--primary)) 0 1px, transparent 1px 96px)",
        }}
      />

      <div className="container relative mx-auto px-4 py-8 md:py-14">
        <div className="mx-auto max-w-3xl">
          <p className="label-micro text-brand">The Manager Pressure Index</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold leading-[0.95] tracking-[-0.03em] text-chrome-foreground md:text-6xl">
            The live pressure index for football managers.
          </h1>
          <p className="mt-4 text-sm text-chrome-muted md:text-base">
            MPI scores every manager from 0 to 100 — how far results have drifted from what their club was
            expected to achieve. Higher means hotter seat.
          </p>
          <div className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:gap-4">
            <Link
              to="/pressure-index"
              className="inline-flex min-h-[44px] items-center text-brand underline underline-offset-4 hover:no-underline sm:min-h-0"
            >
              How it's calculated
            </Link>
            <Link
              to="/sack-race"
              className="inline-flex min-h-[44px] items-center text-brand underline underline-offset-4 hover:no-underline sm:min-h-0"
            >
              Premier League sack race
            </Link>
          </div>
          <p className="mt-2 text-sm text-chrome-muted">
            {leagueCount || 21} domestic leagues tracked · Cup competitions not included · Updated daily
          </p>

          <div className="relative z-30 mt-6 max-w-xl text-foreground [&_input]:h-12 [&_input]:text-foreground [&_input::placeholder]:text-muted-foreground">
            {children}
          </div>

          {hottest.length > 0 && (
            <div className="mt-7 border-t border-chrome-border pt-5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="mb-3 flex w-fit cursor-help items-center gap-2">
                    <Flame className="h-3.5 w-3.5 text-mpi-hot" />
                    <span className="label-micro text-chrome-muted">Hottest seats right now</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Highest current Manager Pressure Index across all tracked leagues
                  {resultsUpTo
                    ? ` · Results up to ${new Date(resultsUpTo).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}`
                    : ""}
                </TooltipContent>

              </Tooltip>

              <div className="grid gap-2 sm:grid-cols-3">
                {hottest.map((entry) => {
                  const tier = getMpiTierDecimal(entry.score);
                  return (
                    <button
                      key={entry.managerId}
                      onClick={() => onSelectManager(entry.managerId, entry.managerSlug ?? undefined)}
                      className="group flex items-center gap-3 rounded-md border border-chrome-border bg-white/[0.03] px-3 py-2.5 text-left transition-colors hover:border-brand/60 hover:bg-white/[0.07]"
                    >
                      <ClubCrest
                        primary={entry.primary} secondary={entry.secondary}
                        clubName={entry.clubName}
                        size="lg"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-sm font-bold text-chrome-foreground">
                          {entry.managerName}
                        </span>
                        <span className="block truncate text-xs text-chrome-muted">
                          {entry.clubName}
                        </span>
                      </span>
                      <span
                        className="tabular num rounded px-2 py-1 text-sm font-extrabold"
                        style={{ backgroundColor: tier.chipBg, color: tier.chipFg }}
                      >
                        {Math.round(entry.score * 100)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default HeroBand;
